import styled, {
  css,
  createGlobalStyle,
  FlattenSimpleInterpolation,
} from "styled-components/macro";

import {
  cardBorder,
  darkModeCardBackgroundColor,
  darkModeCardBorderColor,
  brandPrimary70,
} from "../constants/colors";
import {
  CHART_MAX_WIDTH,
  CARD_BORDER_RADIUS_CSS,
  CARD_INNER_PADDING,
  getIsSafari,
} from "../constants/constants";
import {
  useGetPageFromPath,
  useVisibleGroups,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useCardMargins } from "./useCardMargins";
import { useIsDarkMode } from "store/atoms";
import { DARK_MODE_CSS } from "./DARK_MODE_CSS";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";

export const AppGlobalStyles = createGlobalStyle<{
  $customCss: string | FlattenSimpleInterpolation;
  $isSafari: boolean;
}>`
 ${props => css`
   /* local bug where menus get inline-flex */
   ${process.env.NODE_ENV === "development"
     ? `.MuiMenu-list {
       display: flex;
       justify-content: flex-start;
       flex-direction: column;
     }`
     : ""}
   .process-tile {
     width: 200px;
   }
   ${props.$customCss}
 `}
 ${props =>
   props.$isSafari &&
   css`
     * {
       font-synthesis: none !important; /* Disable font synthesis -> prevents extra-bold font in Safari -> prevent Roboto bold font looking extra-bold -> looks semi-bold instead (other browsers look better with this enabled) */
     }
   `}
  /* on timeline/hublet pages, when calendar is open, ensure timeline is behind it */
  body.date-picker-open #timeAxisSvg {
    z-index: 0 !important;
  }
  body.date-picker-open .TimelinesStyles.tooltipOpen {
    z-index: 0 !important;
  }
  body.date-picker-open .react-calendar {
    z-index: 1000000 !important;
  }
`;

export const AppStylesProvider = ({ children }) => {
  const { isTimelinePage, isTrendsPage, isHubletPage, isProcessTilesPage } =
    useGetPageFromPath();
  const { cardMarginInner, cardMarginOuter } = useCardMargins();

  const visibleProcesses = useVisibleProcesses();
  const visibleGroups = useVisibleGroups();

  const [isDarkMode] = useIsDarkMode();
  const [flightConfig] = useFlightConfig();
  const { flightCustomCss } = useFlagsWithDefaults();
  const customCssFromConfig =
    (flightConfig.general.customCss ?? "") +
    (flightConfig.general.customCssByProcess?.[visibleProcesses[0]?.id ?? ""] ??
      "") +
    (flightConfig.general.customCssByProcessGroup?.[
      visibleGroups[0]?.summaryGroup ?? ""
    ] ?? "") +
    (flightCustomCss ?? "");

  const isSafari = getIsSafari();
  return (
    <>
      <AppGlobalStyles $customCss={customCssFromConfig} $isSafari={isSafari} />
      <AppStyles
        $cardMarginInner={cardMarginInner}
        $cardMarginOuter={cardMarginOuter}
        $isMultiplePagesPrinted={
          isProcessTilesPage || isTimelinePage || isTrendsPage || isHubletPage
        }
        $isDarkMode={isDarkMode}
      >
        {children}
      </AppStyles>
    </>
  );
};

export const AppStyles = styled.div<{
  $customCss?: string;
  $isDarkMode: boolean;
  $isMultiplePagesPrinted: boolean;
  $cardMarginInner: number;
  $cardMarginOuter: number;
}>`
  .cardItem {
    position: relative;
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeCardBackgroundColor : "white"};
    ${CARD_BORDER_RADIUS_CSS}
    border: 1px solid ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeCardBorderColor : cardBorder};
    box-sizing: border-box;
    margin: ${props => props.$cardMarginInner}px
      ${props => props.$cardMarginOuter}px 0;
  }
  .cardPadding {
    padding: ${2.4 * CARD_INNER_PADDING}px ${CARD_INNER_PADDING}px;
  }
  height: 100%;

  @media print {
    @page {
      size: letter;
      margin: 0;
    }
    /* On Chrome (and Safari?) for background graphics to show when printing */
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  &,
  .tilesWrapper,
  .belowTabsContent,
  .dashboardPageContent,
  .belowDividerContent {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    justify-items: flex-start;
    flex-grow: 1;
  }
  .tilesWrapper {
    @media print {
      margin: auto;
      width: ${CHART_MAX_WIDTH - 2 * 30}px;
    }
    padding-bottom: 48px;
  }
  /* to get page-break classes .break-after, .break-before working */
  @media print {
    ${props =>
      props.$isMultiplePagesPrinted
        ? "margin-top: 64px; div { position: static !important; }"
        : ""}
    [class*="NavStyles"],
    [class*="RavenLogoStyles"] {
      position: absolute !important;
    }
    .dateRangeContent,
    .trendsPageDate {
      width: 100%;
    }
    [class*="erd_scroll_detection_container"] {
      /* comes from element-resize-detector, used by useWindowSize - need to hide it when div { position: static } is enabled to remove the spacing */
      display: none;
    }
  }
  .MuiClockPointer-root,
  .MuiClock-pin,
  .MuiClockPointer-thumb {
    background-color: ${brandPrimary70};
  }
  .MuiClockPointer-thumb {
    border-color: ${brandPrimary70};
  }
  .MuiClockNumber-root.Mui-selected {
    color: white;
  }
  ${({ $isDarkMode }) => ($isDarkMode ? DARK_MODE_CSS : "")}
  ${props => props.$customCss}
`;
